import constants from "../../../../appConstants";
import {
  MultiSelectFilter,
  DateTimeFilter,
  StaticFilter,
} from "../../../dataGrid/columnFilters";
import { DropdownEditor, NumericEditor } from "../../../dataGrid/cellEditors";
import {
  DropdownValueCellRenderer,
  StopStatusCellRenderer,
  SpecialNeedsValueGetter,
} from "./../../../dataGrid/cellRenderer/index";
import {
  mergeUserConfig,
  formatDeliveryWindow,
  formatDateTime,
  suppressEnter
} from "../../../dataGrid/gridUtilities";
import {
  SpecialNeedsRenderer,
} from "../../../dataGrid/cellRenderer";
import { isPickupStop, getCustomerName } from "../Common/MergeStops/utills";
import { getFormattedDeliveryDateTime } from "../../utils";
import { getClientSpecificFieldName } from "./StopGridFieldNameConfig";

export default class GridConfig {
  constructor(configuration) {
    const config = configuration || {};
    this.name = "GridConfiguration";
    this.config = GridConfig.getConfiguration(config);
  }

  static enableRowDrag = (params) => {
    return (
      params.api &&
      params.api.selectedRouteNameList &&
      params.api.selectedRouteNameList.length === 1 &&
      params.api.getDisplayedRowCount() > 1 &&
      params.data.stopType !== "AP"
    );
  };

  static routeCellRenderer = (params) => {
    return params.value && params.value.label
      ? params.value.label
      : params.value;
  };

  static isRouteNameEditable = (params) => {
    return !params.data.isLocked && params.data.stopType !== "AP";
  };

  static getConfiguration({
    userConfig,
    featureFlags,
    ROUTENAME_EDITOR_PARAMS,
  }) {
    const defaultConfig = GridConfig.getDefaultConfiguration({
      featureFlags,
      ROUTENAME_EDITOR_PARAMS,
    });
    return mergeUserConfig({
      defaultConfig,
      userConfig,
    });
  }

  static getSpecialNeedsOption(params) {
    return params.context.specialNeedsOptions;
  }

  static getCustomerAddress(params) {
    return isPickupStop(params.data.stopType)
      ? params.data.shipperAddress
      : params.data.consigneeAddress;
  }

  static getCustomerCity(params) {
    return isPickupStop(params.data.stopType)
      ? params.data.shipperCity
      : params.data.city;
  }

  static getCustomerState(params) {
    return isPickupStop(params.data.stopType)
      ? params.data.shipperState
      : params.data.state;
  }

  static getCustomerZip(params) {
    return isPickupStop(params.data.stopType)
      ? params.data.shipperZip
      : params.data.zip;
  }

  static getCustomerCode(params) {
    return isPickupStop(params.data.stopType)
      ? params.data.shipperCode
      : params.data.customerCode;
  }

  static getDefaultConfiguration({ featureFlags, ROUTENAME_EDITOR_PARAMS }) {
    const CLIENT_SPECIFIC_FIELDS = getClientSpecificFieldName(featureFlags.isFFEConfig);
    return {
      title: constants.gridNames.StopsGrid,
      keyColumn: "routeStopID",
      rowSelection: "multiple",
      rowDeselection: "true",
      defaultColDef: {
        sortable: true,
        filter: true,
        resizable: true,
      },
      columns: [
        // {
        //   headerName: "",
        //   minWidth: constants.columnWidth.VIOLATION_DOT,
        //   maxWidth: constants.columnWidth.VIOLATION_DOT,
        //   field: "violation",
        //   pinned: "left",
        //   lockPosition: true,
        //   cellClass: ["gridCell-centerAlign", "gridCell-dot"],
        //   filter: false,
        //   cellRendererFramework: DotRenderer,
        //   isInvisibleInShowHide: true,
        //   gridTitle: constants.gridNames.StopsGrid,
        // },
        {
          headerName: "Seq",
          field: "stopSequence",
          headerTooltip: "Stop Sequence",
          minWidth: constants.columnWidth.STOP_SEQUENCE,
          pinned: "left",
          lockPosition: true,
          cellClass: "gridCell-leftAlign",
          rowDrag: GridConfig.enableRowDrag,
          filterFramework: MultiSelectFilter,
          headerClass: "stopSequenceLabelAlign",
        },
        {
          headerName: "Route",
          field: "routeName",
          headerTooltip: "Route Name",
          pinned: "left",
          lockPosition: true,
          minWidth: constants.columnWidth.ROUTES,
          editable: GridConfig.isRouteNameEditable,
          cellClass: "gridCell-leftAlign",
          filterFramework: MultiSelectFilter,
          cellEditorFramework: DropdownEditor,
          cellEditorParams: {
            canAcceptNewValue: true,
            ...ROUTENAME_EDITOR_PARAMS,
          },
          cellRenderer: DropdownValueCellRenderer,
          suppressKeyboardEvent: suppressEnter,
        },
        {
          headerName: "Ldg Zone",
          field: "loadingZone",
          headerTooltip: "Loading Zone",
          minWidth: constants.columnWidth.LDG_ZONE,
          pinned: "left",
          lockPosition: true,
          cellClass: "gridCell-leftAlign",
          filterFramework: MultiSelectFilter,
        },
        {
          headerName: "Customer",
          field: "consigneeName",
          headerTooltip: "Customer Name",
          minWidth: constants.columnWidth.CONSIGNEE_NAME,
          pinned: "left",
          lockPosition: true,
          cellClass: "gridCell-leftAlign",
          filterFramework: MultiSelectFilter,
          valueGetter : (params) => getCustomerName(params.data),
          tooltipValueGetter: (params) => params.value,
        },
        {
          headerName: "Spl Needs",
          headerTooltip: "Special Needs",
          minWidth: constants.columnWidth.SPL_NEED,
          cellClass: "gridCell-leftAlign",
          valueGetter: SpecialNeedsValueGetter,
          filterFramework: StaticFilter,
          filterParams: {
            getStaticOptions: GridConfig.getSpecialNeedsOption,
          },
          cellRendererFramework: SpecialNeedsRenderer,
          pinned: "left",
          lockPosition: true,
        },
        {
          headerName: "Max IB ETA",
          headerTooltip: "Shipment ETA",
          field: "stopIbeta",
          minWidth: constants.columnWidth.IBETA,
          cellClass: "gridCell-leftAlign",
          valueFormatter: formatDateTime,
          filterFramework: DateTimeFilter,
          filterParams: {
            DATETYPE: constants.DATETYPES.DATETIME,
          },
        },
        {
          headerName: "Address",
          field: "consigneeAddress",
          headerTooltip: "Customer Address",
          minWidth: constants.columnWidth.CUSTOMER_ADDRESS,
          cellClass: "gridCell-leftAlign",
          filterFramework: MultiSelectFilter,
          valueGetter : GridConfig.getCustomerAddress,
          tooltipValueGetter: (params) => params.value,
        },
        {
          headerName: "City",
          headerTooltip: "City",
          field: "city",
          cellClass: "gridCell-leftAlign",
          minWidth: constants.columnWidth.CITY,
          filterFramework: MultiSelectFilter,
          valueGetter : GridConfig.getCustomerCity,
          tooltipValueGetter: (params) => params.value,
        },
        {
          headerName: "State",
          headerTooltip: "State",
          field: "state",
          cellClass: "gridCell-leftAlign",
          minWidth: constants.columnWidth.STATE,
          filterFramework: MultiSelectFilter,
          valueGetter : GridConfig.getCustomerState,
        },
        {
          headerName: "Zip",
          field: "zip",
          headerTooltip: "Zip",
          minWidth: constants.columnWidth.ZIP_CODE,
          cellClass: "gridCell-leftAlign",
          filterFramework: MultiSelectFilter,
          valueGetter : GridConfig.getCustomerZip,
        },
        {
          headerName: "Bills",
          field: "bills",
          headerTooltip: "Bills",
          minWidth: constants.columnWidth.BILLS,
          cellClass: "gridCell-leftAlign",
          filterFramework: MultiSelectFilter,
        },
        {
          headerName: "HU",
          headerTooltip: "Handling Unit",
          field: "totalHu",
          minWidth: constants.columnWidth.HU,
          cellClass: "gridCell-leftAlign",
          filterFramework: MultiSelectFilter,
        },
        {
          headerName: "PSE",
          field: "pse",
          minWidth: constants.columnWidth.PSE,
          //editable: GridConfig.isEditable,
          headerTooltip: "Pallet Space Equivalent",
          cellClass: "gridCell-leftAlign",
          filterFramework: MultiSelectFilter,
          //tooltipField: "pse"
          hide: featureFlags.IsWeightBasedRoutingEnabled,
        },
        {
          headerName: "Calculated PSE",
          field: "calculatedPse",
          minWidth: constants.columnWidth.CALCULATED_PSE,
          headerTooltip: "Calculated Pallet Space Equivalent",
          cellClass: "gridCell-leftAlign",
          filterFramework: MultiSelectFilter,
          hide: true,
        },
        {
          headerName: "Weight",
          field: "totalWeight",
          headerTooltip: "Weight",
          minWidth: constants.columnWidth.WEIGHT,
          cellClass: "gridCell-centerAlign",
          filterFramework: MultiSelectFilter,
        },
        // {
        //   headerName: "Service",
        //   field: "priority",
        //   headerTooltip: "Service",
        //   minWidth: constants.columnWidth.PRIORITY,
        //   cellClass: "gridCell-leftAlign",
        //   filterFramework: MultiSelectFilter
        // },
        // {
        //   headerName: "LG",
        //   headerTooltip: "Lift Gate",
        //   field: "liftgate",
        //   minWidth: constants.columnWidth.LG,
        //   cellClass: "gridCell-leftAlign",
        //   filterFramework: MultiSelectFilter
        // },
        // {
        //   headerName: "Equip Pref",
        //   field: "eqptPref",
        //   headerTooltip: "Equipment Preference",
        //   minWidth: constants.columnWidth.EQUIPMENT_PREFERENCE,
        //   cellClass: "gridCell-leftAlign",
        //   filterFramework: MultiSelectFilter
        // },
        {
          headerName: CLIENT_SPECIFIC_FIELDS["deliveryWindow"],
          field: "deliveryWindow",
          headerTooltip: CLIENT_SPECIFIC_FIELDS["deliveryWindow"],
          minWidth: constants.columnWidth.DELIVERY_WINDOW ,
          cellClass: "gridCell-leftAlign",
          filterFramework: DateTimeFilter,
          filterParams: {
            DATETYPE: constants.DATETYPES.DATETIME,
            isTimeInterval: true,
            useGetterValue: true,
          },
          editable: false,
          valueFormatter: formatDeliveryWindow,
        },
        {
          headerName: CLIENT_SPECIFIC_FIELDS["eta"],
          field: "eta",
          headerTooltip: CLIENT_SPECIFIC_FIELDS["eta"],
          minWidth: constants.columnWidth.ETA + 50,
          cellClass: GridConfig.timeViolation,
          cellClass: "gridCell-leftAlign",
          filterFramework: DateTimeFilter,
          filterParams: {
            DATETYPE: constants.DATETYPES.DATETIME,
          },
        },
        {
          headerName: CLIENT_SPECIFIC_FIELDS["etd"],
          field: "etd",
          headerTooltip: CLIENT_SPECIFIC_FIELDS["etd"],
          minWidth: constants.columnWidth.ETD + 50,
          cellClass: "gridCell-leftAlign",
          filterFramework: DateTimeFilter,
          filterParams: {
            DATETYPE: constants.DATETYPES.DATETIME,
          },
        },
        ...(featureFlags.isFFEConfig ? [{
          headerName: "Service DateTime",
          field : "deliveryDateTime",
          valueGetter : (params) => getFormattedDeliveryDateTime(params.data.deliveryDateTime),
          headerTooltip: "Service DateTime",
          minWidth: constants.columnWidth.W_160,
          cellClass: "gridCell-leftAlign",
          filterFramework: DateTimeFilter,
          filterParams: {
            DATETYPE: constants.DATETYPES.DATETIME,
          },
        }]: []),
        {
          headerName: "Status",
          field: "stopStatus",
          headerTooltip: "Stop Status",
          minWidth: constants.columnWidth.STOP_STATUS,
          cellClassRules: {
            violation: (params) =>
              params.value === constants.STOP_STATUS.DELAYED,
          },
          filterFramework: MultiSelectFilter,
          valueGetter: StopStatusCellRenderer,
        },
        {
          headerName: "Stop Time (Mins)",
          field: "stopTime",
          headerTooltip: "Stop Time",
          editable: featureFlags.IsStopTimeEditable,
          minWidth: constants.columnWidth.STOP_TIME,
          cellClass: "gridCell-leftAlign",
          filterFramework: MultiSelectFilter,
          cellEditorFramework: NumericEditor,
        },
        {
          headerName: "Rem Duty",
          field: "dutyTime",
          headerTooltip: "Remaining Duty Time",
          minWidth: constants.columnWidth.W_90,
          cellClass: "gridCell-leftAlign",
          filterFramework: MultiSelectFilter,
        },
        {
          headerName: "Cubic Feet",
          headerTooltip: "Cubic Feet",
          field: "cubesCount",
          minWidth: constants.columnWidth.CUBIC_FEET,
          cellClass: "gridCell-centerAlign",
          filterFramework: MultiSelectFilter,
        },
        {
          headerName: "Customer#",
          headerTooltip: "Customer Account Number",
          minWidth: constants.columnWidth.CUSTOMER_CODE,
          cellClass: "gridCell-leftAlign",
          filterFramework: MultiSelectFilter,
          valueGetter : GridConfig.getCustomerCode,
          tooltipValueGetter: (params) => params.value,
          hide: true,
        },
        {
          headerName: "Latitude",
          field: "latitude",
          minWidth: constants.columnWidth.LATITUDE,
          //editable: GridConfig.isEditable,
          headerTooltip: "Latitude",
          cellClass: "gridCell-leftAlign",
          filterFramework: MultiSelectFilter,
          tooltipField: "latitude",
          hide: true,
        },
        {
          headerName: "Longitude",
          field: "longitude",
          minWidth: constants.columnWidth.LONGITUDE,
          //editable: GridConfig.isEditable,
          headerTooltip: "Longitude",
          cellClass: "gridCell-leftAlign",
          filterFramework: MultiSelectFilter,
          tooltipField: "longitude",
          hide: true,
        },
        // {
        //   headerName: "Special Instructions",
        //   field: "specialInstruction",
        //   minWidth: constants.columnWidth.SPECIAL_INSTRUCTION,
        //   headerTooltip : "Special Instructions",
        //   cellClass: "gridCell-leftAlign",
        //   filterFramework: MultiSelectFilter,
        //   tooltipField: "specialInstruction"
        // },
        {
          headerName: "Pieces",
          headerTooltip: "Pieces",
          field: "totalPieces",
          minWidth: constants.columnWidth.PIECES,
          cellClass: "gridCell-leftAlign",
          filterFramework: MultiSelectFilter,
        },
        {
          headerName: "Pallets",
          headerTooltip: "Pallets",
          field: "totalPallets",
          minWidth: constants.columnWidth.PIECES,
          cellClass: "gridCell-leftAlign",
          filterFramework: MultiSelectFilter,
        },
        {
          headerName: "Frozen Pallets",
          field: "freezerPlts",
          headerTooltip: "Frozen Pallets",
          minWidth: constants.columnWidth.FROZEN_PALLETS,
          cellClass: "gridCell-leftAlign",
          filterFramework: MultiSelectFilter,
          tooltipField:"freezerPlts"
        },
        {
          headerName: "Non-Frozen Pallets",
          field: "coolerPlts",
          headerTooltip: "Non-Frozen Pallets",
          minWidth: constants.columnWidth.NON_FROZEN_PALLETS,
          cellClass: "gridCell-leftAlign",
          filterFramework: MultiSelectFilter,
          tooltipField:"coolerPlts"
        },
        {
          headerName: "Temp Type",
          field: "tempType",
          headerTooltip: "Temp Type",
          minWidth: constants.columnWidth.TEMP_TYPE,
          cellClass: "gridCell-leftAlign",
          filterFramework: MultiSelectFilter,
          tooltipField:"tempType"
        },
        {
          headerName: "Max Trailer",
          headerTooltip: "Max Feasible Trailer Length",
          field: "maxFeasibleTrailerLength",
          editable: false,
          minWidth: constants.columnWidth.MAX_TRAILER,
          cellClass: "gridCell-leftAlign",
          filterFramework: MultiSelectFilter,
        },
        {
          headerName: "Comments",
          headerTooltip: "Comments",
          field: "comments",
          minWidth: constants.columnWidth.MAX_TRAILER,
          cellClass: "gridCell-leftAlign",
          filterFramework: MultiSelectFilter,
        },
        {
          headerName: "Stop Type",
          headerTooltip: "Stop Type",
          hide : true,
          field: "stopType",
          minWidth: constants.columnWidth.MAX_TRAILER,
          cellClass: "gridCell-leftAlign",
          filterFramework: MultiSelectFilter,
        },
        {
          headerName: "Distance From PrevStop",
          headerTooltip: "Distance From Previous Stop",
          field: "distanceFromPrevStop",
          minWidth: constants.columnWidth.W_200,
          cellClass: "gridCell-leftAlign",
          filterFramework: MultiSelectFilter,
        }
      ],
    };
  }
}
